import React, { createContext, useContext } from "react";

/**
 * Add element context type definition
 * @typedef {Object} AddTarget
 * @property {number} [componentId] - Component to add item to
 * @property {number} [heroId] - Hero to add item to (alternative to componentId)
 * @property {number} [buttonCollectionId] - ButtonCollection to add button to
 * @property {number} [pageId] - Page to add component to
 * @property {number} order - Position to insert at (0-indexed)
 * @property {number} [websiteId] - Website ID (for page-level adds)
 * @property {string} [restrictToType] - Restrict selection to specific type (e.g. 'button')
 */

/**
 * @typedef {Object} AddElementContextType
 * @property {boolean} enabled - Whether add element mode is enabled
 * @property {(target: AddTarget, anchorRect?: DOMRect) => void} openAddModal - Open add modal
 */

export const AddElementContext = createContext({
  enabled: false,
  openAddModal: (target, anchorRect) => {
    console.warn('openAddModal called outside AddElementProvider', { target, anchorRect });
  }
});

export const AddElementProvider = ({ children, openAddModal }) => {
  const contextValue = {
    enabled: true,
    openAddModal: (target, anchorRect) => {
      console.log('🔍 ADDBUTTON_ORDER_BUG - AddElementContext validation:', {
        target,
        hasTarget: !!target,
        hasOrder: typeof target?.order === 'number',
        orderValue: target?.order,
      });
      
      // Validate target
      if (!target) {
        console.error('🔍 ADDBUTTON_ORDER_BUG - ERROR: target is required');
        return;
      }
      
      // Ensure order is provided (default to 0 if missing)
      if (typeof target.order !== 'number') {
        console.warn('🔍 ADDBUTTON_ORDER_BUG - WARNING: order not specified, defaulting to 0', target);
        target = { ...target, order: 0 };
      }
      
      // Validate parent specification
      const hasComponent = target.componentId !== undefined;
      const hasHero = target.heroId !== undefined;
      const hasButtonCollection = target.buttonCollectionId !== undefined;
      const hasPage = target.pageId !== undefined;
      
      if (!hasComponent && !hasHero && !hasButtonCollection && !hasPage) {
        console.error('🔍 ADDBUTTON_ORDER_BUG - ERROR: must specify componentId, heroId, buttonCollectionId, or pageId', target);
        return;
      }
      
      console.log('🔍 ADDBUTTON_ORDER_BUG - AddElementContext validation passed, calling openAddModal');
      openAddModal(target, anchorRect);
    }
  };
  
  return (
    <AddElementContext.Provider value={contextValue}>
      {children}
    </AddElementContext.Provider>
  );
};

export const useAddElement = () => {
  const context = useContext(AddElementContext);
  if (!context.enabled) {
    console.warn('useAddElement called outside AddElementProvider');
  }
  return context;
}; 